import { localeQueries } from '@/features/locale/queries';
import { isPublicViewer, subscribePublicViewer } from '@/features/locale/session';
import * as React from 'react';
import { QueryClientProvider, useQuery } from '@tanstack/react-query';
import { queryClient } from '@/lib/query/query-client';
import { useAuthStore } from '@/store/auth-store';
import { realtimeManager } from '@/lib/realtime/realtime-manager';
import { startSelfPresence, stopSelfPresence, usePresenceStore } from '@/lib/presence';
import { inboxQueries } from '@/features/inbox/queries';
import { usePermissionsStore } from '@/store/permissions-store';
import { useVoicePresenceStore } from '@/features/voice/voice-presence-store';
import { useVoiceSessionStore } from '@/features/voice/voice-session-store';
import { useVoiceRoomStore } from '@/features/voice/voice-room-store';
import { useCallStore } from '@/features/calls/call-store';
import { useMessageHistoryStore } from '@/features/inbox/message-history-store';
import { CallOverlay } from '@/features/calls/components/CallOverlay';
import { VoiceDock } from '@/features/voice/components/VoiceDock';
import { CommsHub } from '@/features/team-chat/components/CommsHub';
import { useChatDockStore } from '@/features/team-chat/chat-dock-store';
import { useTeamChatLiveAlertStore } from '@/features/team-chat/live-alert-store';
import { usePersonalIdentity } from '@/features/appearance/session';
import { appearanceQueries } from '@/features/appearance/queries';
import { moduleQueries } from '@/features/modules/queries';
import { notificationQueries } from '@/features/settings/notification-queries';
import { notificationSound } from '@/lib/notifications/sound-service';
import { closeAllNotifications } from '@/lib/notifications/browser-service';
import { isMobileBrowser } from '@/components/layout/mobile-access';

function AuthInitializer({ children }: { children: React.ReactNode }) {
  const publicView = React.useSyncExternalStore(subscribePublicViewer, isPublicViewer, isPublicViewer);
  const token = useAuthStore((state) => state.token);
  const impersonation = useAuthStore((state) => state.impersonation);
  usePersonalIdentity();
  const appearance=appearanceQueries.preferences();
  useQuery({...appearance,enabled:appearance.enabled&&!publicView});
  useQuery({ ...localeQueries.preference(), enabled: !!token && !impersonation && !publicView });
  // Keep an active observer for effective permissions throughout the session.
  // Realtime reconnects invalidate this key, so role changes take effect
  // without requiring a logout or reading a stale JWT role.
  useQuery({
    ...inboxQueries.myPermissions(),
    enabled: !!token && !publicView,
  });
  // Notification preferences must be observed app-wide. Previously they were
  // fetched only after visiting Settings, so a saved sound/DM/channel choice
  // was ignored for most sessions and realtime silently fell back to defaults.
  useQuery({
    ...notificationQueries.settings(),
    enabled: !!token && !publicView,
  });

  React.useEffect(() => {
    if (publicView) {
      stopSelfPresence({ announceOffline: false });
      realtimeManager.disconnect();
      return;
    }
    if (token) {
      realtimeManager.connect();
      // Web Audio is blocked until a user gesture. Keep the unlock listener in
      // the authenticated app shell so chat sounds work from every route, not
      // only after the user opens Settings and presses “Probar”.
      const unlockSound = () => notificationSound.unlock();
      document.addEventListener('pointerdown', unlockSound, { once: true, capture: true });
      document.addEventListener('keydown', unlockSound, { once: true, capture: true });
      // Announce self-presence and start idle detection. Order matters:
      // realtime first so the broadcast we trigger lands in our own store.
      startSelfPresence();
      // Prefetch the agents directory once after login so the presence store
      // is hydrated regardless of which route the user lands on. Without
      // this, screens like /reports show everyone as offline until somebody
      // navigates to inbox or settings and triggers the query naturally.
      void queryClient.prefetchQuery(inboxQueries.agents());
      // The active permissions query above hydrates teams + effective flags.
      // Module gates drive tenant-scoped navigation. Prefetch so optional
      // modules such as the Knowledge Base don't flash in the sidebar. The
      // map is tenant-scoped, so discard a previous session/impersonation's
      // fresh cache before loading the current token's flags.
      queryClient.removeQueries({ queryKey: ['me', 'modules'] });
      void queryClient.prefetchQuery(moduleQueries.myModules());
      return () => {
        document.removeEventListener('pointerdown', unlockSound, { capture: true });
        document.removeEventListener('keydown', unlockSound, { capture: true });
      };
    } else {
      stopSelfPresence();
      realtimeManager.disconnect();
      usePresenceStore.getState().clear();
      usePermissionsStore.getState().clear();
      useVoicePresenceStore.getState().clear();
      useVoiceSessionStore.getState().clear();
      useVoiceRoomStore.getState().reset();
      useChatDockStore.getState().clear();
      useTeamChatLiveAlertStore.getState().clear();
      closeAllNotifications();
      useCallStore.getState().clear();
      // Ventanas de historial del inbox: son por conversación y por tenant, y
      // el próximo login no tiene por qué heredar cuánto había scrolleado otro.
      useMessageHistoryStore.getState().clear();
      queryClient.removeQueries({ queryKey: ['me', 'modules'] });
      queryClient.removeQueries({ queryKey: ['notifications', 'settings'] });
    }
  }, [token, publicView]);

  if (publicView) return children;

  return (
    <>
      {children}
      {/* App-wide so incoming calls + the in-call room work on any route. */}
      <CallOverlay />
      {/* App-wide voice channel session — persists across navigation. */}
      <VoiceDock />
      {/* App-wide unified comms hub — single launcher for team chat, voice
          channels, and calls (replaces the standalone chat dock). */}
      <CommsHub />
    </>
  );
}

export function Providers({ children }: { children: React.ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      {/* Guest queries keep their provider on phones. A persisted agent session
          must not start private polling, presence, calls or sockets there. */}
      {isMobileBrowser() ? children : <AuthInitializer>{children}</AuthInitializer>}
    </QueryClientProvider>
  );
}
